import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Spinner } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import CountryList from "../CountryList";
import { appleApi } from "../../services/api";
import { FaGlobeAmericas, FaSearch } from "react-icons/fa";

import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
);

const PackageForm = () => {
  const [appId, setAppId] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false); // New loading state

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Set loading to true

    const dataToSend = {
      app_id: appId,
      country: countryCode,
    };

    try {
      const { data: result } = await appleApi.get("get-app-data/", {
          params: dataToSend,
        });
      setResponse(result ?? {});
    } catch (error) {
      console.error("Error getting app data:", error);
      setResponse("API error occurred");
    } finally {
      setLoading(false); // Set loading to false
    }
  };

  // Chart Data for Localization
  const ratingsChartData = {
    labels: ["Ratings"],
    datasets: [
      {
        label: "Ratings",
        data: [response?.localized_data?.ratings || 0],
        backgroundColor: "rgba(153, 102, 255, 0.6)",
      },
    ],
  };

  const scoreChartData = {
    labels: ["Score"],
    datasets: [
      {
        label: "Score",
        data: [response?.localized_data?.score || 0],
        backgroundColor: "rgba(255, 159, 64, 0.6)",
      },
    ],
  };

  // Chart Data for ASO Audit
  const averageRatingChartData = {
    labels: ["Average Rating"],
    datasets: [
      {
        label: "Average Rating",
        data: [response?.aso_audit?.rating_analysis?.average_rating || 0],
        backgroundColor: "rgba(54, 162, 255, 0.6)",
      },
    ],
  };

  const ratingCountChartData = {
    labels: ["Rating Count"],
    datasets: [
      {
        label: "Rating Count",
        data: [response?.aso_audit?.rating_analysis?.rating_count || 0],
        backgroundColor: "rgba(255, 206, 86, 0.6)",
      },
    ],
  };

  const totalScreenshotsChartData = {
    labels: ["Total Screenshots"],
    datasets: [
      {
        label: "Total Screenshots",
        data: [response?.aso_audit?.screenshot_quality?.total_screenshots || 0],
        backgroundColor: "rgba(255, 99, 132, 0.6)",
      },
    ],
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <div className="flex items-center mb-6">
          <FaGlobeAmericas className="text-blue-600 text-2xl mr-3" />
          <h2 className="text-2xl font-bold text-gray-800">Localized ASO</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App ID
              </label>
              <input
                type="text"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                placeholder="App ID (e.g. 12345678)"
                required
              />
            </div>

            <div className="md:col-span-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <CountryList
                onSelect={(selectedCountry) => setCountryCode(selectedCountry)}
              />
            </div>

            <div className="md:col-span-2 flex items-end">
              <button
                type="submit"
                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <FaSearch className="mr-2" /> Submit
              </button>
            </div>
          </div>
        </form>
      </div>

      {loading && (
        <div className="flex justify-center items-center h-64">
          <Spinner animation="border" role="status" className="text-blue-600" />
          <span className="ml-3 text-lg text-gray-600 font-medium">Loading... Please Wait</span>
        </div>
      )}

      {/* Display the API response in table format */}
      {response && !loading && (
        <div className="mt-8 space-y-8">
          {response.localized_data ? (
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                <span className="bg-blue-100 text-blue-600 p-2 rounded-lg mr-3">
                  <FaGlobeAmericas />
                </span>
                Localized Data
              </h4>
              
              <div className="overflow-hidden rounded-lg border border-gray-200 mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {[
                      { label: "Title", value: response.localized_data.title },
                      { label: "Description", value: response.localized_data.description },
                      { label: "Summary", value: response.localized_data.summary },
                      { label: "Developer", value: response.localized_data.developer },
                      { 
                        label: "Updated", 
                        value: response.localized_data.updated
                          ? new Date(response.localized_data.updated).toLocaleString()
                          : "Invalid Date"
                      },
                      { label: "Version", value: response.localized_data.version },
                      { label: "Currency", value: response.localized_data.currency },
                      { label: "Price", value: response.localized_data.price },
                    ].map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50 w-1/4">{item.label}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 break-words max-w-3xl">{item.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Display the charts for localization */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h5 className="text-center font-semibold mb-4">Ratings Distribution</h5>
                  <Bar data={ratingsChartData} options={{ responsive: true }} />
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h5 className="text-center font-semibold mb-4">Score Analysis</h5>
                  <Bar data={scoreChartData} options={{ responsive: true }} />
                </div>
              </div>

              {/* Display screenshots */}
              {response.localized_data.screenshots &&
                response.localized_data.screenshots.length > 0 && (
                  <div className="mt-6">
                    <h5 className="font-bold text-lg mb-4">Screenshots</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {response.localized_data.screenshots.map(
                        (screenshot, index) => (
                          <div className="rounded-lg overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-shadow" key={index}>
                            <img
                              src={screenshot}
                              alt={`Screenshot ${index + 1}`}
                              className="w-full h-auto object-cover"
                            />
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <p className="text-yellow-700">No localized data available.</p>
            </div>
          )}

          {/* ASO Audit Data */}
          {response.aso_audit && (
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                <span className="bg-green-100 text-green-600 p-2 rounded-lg mr-3">
                  <FaSearch />
                </span>
                ASO Audit Data
              </h4>
              
              <div className="overflow-hidden rounded-lg border border-gray-200 mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50 w-1/4">Keywords Found</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                          {response.aso_audit.keywords.keywords_found.map(
                            (keyword, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold"
                              >
                                {keyword}
                              </span>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                    {[
                      { label: "Total Keywords", value: response.aso_audit.keywords.keyword_count },
                      { label: "Average Rating", value: response.aso_audit.rating_analysis.average_rating },
                      { label: "Ratings Count", value: response.aso_audit.rating_analysis.rating_count },
                      { label: "Total Screenshots", value: response.aso_audit.screenshot_quality.total_screenshots },
                      { label: "High Quality Screenshots", value: response.aso_audit.screenshot_quality.high_quality },
                      { label: "Sentiment", value: response.aso_audit.review_sentiment.sentiment },
                      { label: "Sentiment Score", value: response.aso_audit.review_sentiment.score },
                    ].map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50">{item.label}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Display the charts for ASO audit */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h5 className="text-center font-semibold mb-4">Average Rating</h5>
                  <Bar data={averageRatingChartData} options={{ responsive: true }} />
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h5 className="text-center font-semibold mb-4">Rating Count</h5>
                  <Bar data={ratingCountChartData} options={{ responsive: true }} />
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h5 className="text-center font-semibold mb-4">Total Screenshots</h5>
                  <Bar data={totalScreenshotsChartData} options={{ responsive: true }} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PackageForm;

